import { useState, useEffect, useCallback } from 'react'
import { NumericFormat } from 'react-number-format'
import Moment from 'react-moment'
import { useSearchParams } from 'react-router-dom'
import {
    CircularProgress, Container, Grid2 as Grid, Box,
    Pagination, TextField, InputAdornment, Typography
} from '@mui/material'

import SearchIcon from '@mui/icons-material/Search'
import NorthIcon from '@mui/icons-material/North'
import SouthIcon from '@mui/icons-material/South'

import ButtonIcon from './ButtonIcon'
import '../../assets/css/table.css'

function IconoDelOrder({ orden }) {
    if (orden === 0) return <NorthIcon style={{ fontSize: '0.8rem' }} />
    if (orden === 1) return <SouthIcon style={{ fontSize: '0.8rem' }} />
    return null
}

export default function TablaPaginada2026({
    api,
    thead,
    tbody,
    orderDefault,
    itemsPerPageDefault = 25,
    extraParams = {},
    buscador = true
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    // 1. Lectura de parámetros desde URL
    const page = parseInt(searchParams.get("page") || "1");
    const textsearch = searchParams.get("search") || "";
    const orderRaw = searchParams.get("order");
    const order = orderRaw ? orderRaw.split(',').map(Number) : orderDefault;

    // 2. Estado local para el buscador (Debounce)
    const [localSearch, setLocalSearch] = useState(textsearch);

    const updateQueryParams = useCallback((newParams) => {
        const params = Object.fromEntries(searchParams.entries());
        const updated = { ...params, ...newParams };
        if (!updated.search) delete updated.search;
        if (updated.page === "1") delete updated.page;
        setSearchParams(updated, { replace: true });
    }, [searchParams, setSearchParams]);

    // 3. Efecto Debounce: Sincroniza el input visual con la URL tras 500ms de inactividad
    useEffect(() => {
        const handler = setTimeout(() => {
            if (localSearch !== textsearch) {
                updateQueryParams({ search: localSearch, page: 1 });
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [localSearch, textsearch, updateQueryParams]);

    // 4. Ejecución del Query (Automática vía RTK Query)
    const {
        data: returnedData,
        isLoading,
        isFetching
    } = api({
        page,
        textsearch,
        sort: order[0],
        dir: order[1] === 0 ? 'ASC' : 'DESC',
        ...extraParams
    });

    const ordenarPorColumna = (posicion) => {
        if (thead[posicion].ordenable) {
            let newOrder = order[0] === posicion
                ? [posicion, (order[1] > 0 ? 0 : 1)]
                : [posicion, 1];
            updateQueryParams({ order: newOrder.join(',') });
        }
    };

    useEffect(() => {
        // Si cambian los filtros extra (como las fechas), volvemos a la página 1
        if (page !== 1) {
            updateQueryParams({ page: 1 });
        }
    }, [extraParams]); // Escucha cambios en el objeto de filtros

    const showingLoader = isLoading || isFetching;

    return (
        <Container className="containerBigTable" component="main" maxWidth="lg">
            <Grid container spacing={0} sx={{ mb: 1 }}>
                {/* Izquierda: Contador de registros */}
                <Grid
                    size={{ xs: 12, md: 9 }}
                    sx={{
                        textAlign: { xs: 'center', md: 'left' },
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    {!isLoading && returnedData ? (
                        <div className="contador-registros">
                            {returnedData.totalDocs === 0 && <p>No se encontraron registros.</p>}
                            {returnedData.totalDocs === 1 && <p>Se encontró {returnedData.totalDocs} registro.</p>}
                            {returnedData.totalDocs > 1 && <p>Se encontraron {returnedData.totalDocs} registros.</p>}
                        </div>
                    ) : (
                        <p>&nbsp;</p>
                    )}
                </Grid>

                {/* Derecha: Buscador */}
                {buscador !== false && (
                    <Grid className="titulo" size={{ xs: 12, md: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                            <TextField
                                autoComplete='off'
                                // Usamos el estado local para que la escritura sea instantánea
                                value={localSearch}
                                onChange={(e) => setLocalSearch(e.target.value)}
                                fullWidth
                                label="Buscar..."
                                variant="outlined"
                                size='small'
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>

            <div className="tableContainer" style={{ position: 'relative', minHeight: '150px' }}>
                {showingLoader && (
                    <Box sx={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.6)', zIndex: 10,
                        display: 'flex', justifyContent: 'center', alignItems: 'center',
                    }}>
                        <CircularProgress />
                    </Box>
                )}

                <table
                    className="formato1"
                    cellSpacing="0"
                    cellPadding="0"
                    style={{
                        opacity: showingLoader ? 0.4 : 1,
                        transition: 'opacity 0.2s'
                    }}
                >
                    <thead>
                        <tr>
                            {thead.map((element, index) => (
                                <th
                                    key={index}
                                    align={element.align}
                                    onClick={() => ordenarPorColumna(index)}
                                    className={element.ordenable ? "ordenable" : ""}
                                    style={{ cursor: element.ordenable ? 'pointer' : 'default' }}
                                >
                                    {element.label}
                                    {order[0] === index && <IconoDelOrder orden={order[1]} />}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading && returnedData?.docs?.length === 0 ? (
                            <tr>
                                <td colSpan={thead.length} style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                                    No hay datos disponibles para mostrar.
                                </td>
                            </tr>
                        ) : (
                            returnedData?.docs?.map((registro, idx) => (
                                <tr key={registro.codigo || idx}>
                                    {tbody.map((elemento, indexTd) => (
                                        <td key={indexTd} align={thead[indexTd].align}>

                                            {elemento.tipo === "texto" && registro[elemento.valor]}

                                            {elemento.tipo === "fechahora" && <Moment format="DD-MM-YYYY HH:mm:ss" date={registro[elemento.valor]} />}
                                            {elemento.tipo === "fecha" && <Moment format="DD-MM-YYYY" date={registro[elemento.valor]} />}

                                            {elemento.tipo === "precio" && (
                                                <NumericFormat
                                                    value={registro[elemento.valor]}
                                                    displayType={'text'}
                                                    decimalSeparator={elemento.options[0]}
                                                    thousandSeparator={elemento.options[1]}
                                                    prefix={elemento.options[2]}
                                                    decimalScale={0}
                                                />
                                            )}

                                            {elemento.tipo === "funcionEspecial" && (
                                                elemento.funcion(registro[elemento.valor])
                                            )}

                                            {elemento.tipo === "porcentaje" && (
                                                <NumericFormat
                                                    value={registro[elemento.valor]}
                                                    displayType={'text'}
                                                    decimalSeparator={elemento.options[0]}
                                                    thousandSeparator={elemento.options[1]}
                                                    suffix={' %'}
                                                    decimalScale={2}
                                                    fixedDecimalScale={true}
                                                />
                                            )}

                                            {elemento.tipo === "progreso" && (() => {
                                                // Calculamos los valores una sola vez
                                                const porcentaje = Math.min(Math.max(Number(registro[elemento.valor]) || 0, 0), 100);
                                                const textoValor = `${porcentaje.toFixed(elemento.decimales || 0)}%`;

                                                // Determinamos el color de MUI según los umbrales
                                                const colorBarra = porcentaje >= (elemento.umbrales?.alto || 75) ? 'success.main' :
                                                    porcentaje >= (elemento.umbrales?.medio || 40) ? 'warning.main' : 'error.main';

                                                return (
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            minWidth: '80px',
                                                            height: 22,
                                                            backgroundColor: 'rgba(0, 0, 0, 0.08)', // Fondo de la barra vacía
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            // Magia CSS: Convertimos este Box en un contenedor de referencia
                                                            containerType: 'inline-size'
                                                        }}
                                                    >
                                                        {/* 1. Capa de Texto Base (Oscuro, se ve en la parte VACÍA de la barra) */}
                                                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1 }}>
                                                                {textoValor}
                                                            </Typography>
                                                        </Box>

                                                        {/* 2. Capa de la Barra de Progreso (Crece según el porcentaje) */}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0, left: 0, bottom: 0,
                                                                width: `${porcentaje}%`,
                                                                bgcolor: colorBarra,
                                                                overflow: 'hidden', // Esto "corta" el texto blanco sobrante
                                                                transition: 'width 0.4s ease'
                                                            }}
                                                        >
                                                            {/* 3. Capa de Texto Superpuesto (Blanco, se ve DENTRO de la barra) */}
                                                            <Box
                                                                sx={{
                                                                    // Magia CSS: 100cqi fuerza a este contenedor interno a tener 
                                                                    // el ancho EXACTO del Box principal, manteniendo el texto centrado.
                                                                    width: '100cqi',
                                                                    height: '100%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center'
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#fff', lineHeight: 1 }}>
                                                                    {textoValor}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                );
                                            })()}

                                            {elemento.tipo === "botones" && elemento.valor.map((unBoton, indexBoton) => {
                                                if (unBoton.componentePersonalizado) {
                                                    return (
                                                        <span key={indexBoton} style={{ marginRight: '8px' }}>
                                                            {unBoton.componentePersonalizado(registro)}
                                                        </span>
                                                    )
                                                }

                                                const tieneRuta = Boolean(unBoton.ruta);
                                                const destino = tieneRuta ? (unBoton.ruta + registro[unBoton.valor]) : undefined;

                                                return (
                                                    <ButtonIcon
                                                        key={indexBoton}
                                                        variant="outlined"
                                                        size="small"
                                                        color={unBoton.color}
                                                        startIcon={unBoton.icono}
                                                        to={destino}
                                                        state={unBoton.state}
                                                        noLink={!tieneRuta}
                                                        handleClick={unBoton.funcionEspecial ? () => unBoton.funcionEspecial(registro) : undefined}
                                                        sx={{ mr: 1 }}
                                                    />
                                                )
                                            })}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <Pagination
                className="table-pagination"
                count={returnedData?.totalPages || 0}
                page={page}
                onChange={(_, val) => updateQueryParams({ page: val })}
                disabled={showingLoader}
                sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
            />
        </Container>
    )
}